import { registerFocusable } from '@tamagui/focusable';
import { X } from '@tamagui/lucide-icons';
import { InputProps } from '@unistocks/components/basic/input';
import {
  FormFieldInjectedProps,
  withLabelAndValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps } from '@unistocks/components/form/model';
import { SelectTrigger } from '@unistocks/components/select/trigger';
import { Typeahead, TypeaheadProps, TypeaheadRef } from '@unistocks/components/typeahead/typeahead';
import { isFunction } from '@unistocks/shared/utils/util';
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { ListItemProps, useComposedRefs } from 'tamagui';

export type FormTypeaheadProps<
  TValue = any,
  TFieldValues extends FieldValues = FieldValues
> = TypeaheadProps<TValue> &
  Omit<ListItemProps, FormFieldOmittedProps | 'icon' | 'filter' | 'onChange' | 'ref'> &
  Omit<
    InputProps,
    FormFieldOmittedProps | 'onChange' | 'onChangeText' | 'onBlur' | 'value' | 'filter' | 'ref'
  > &
  FormComponentPropsBase<TFieldValues>;

const FormTypeaheadComponent: React.FC<FormTypeaheadProps & FormFieldInjectedProps> = ({
  ref: refProp,
  name,
  id,
  field,
  fieldState,
  isRequired,
  isDisabled,
  placeholder,
  valueFormatter,
  onChange: onSelect,
  valueTransform,
  dependsOn,
  ...props
}) => {
  const composedRef = useComposedRefs(refProp, field.ref);

  const value = valueTransform?.input?.(field.value) ?? field.value;

  const typeaheadRef = useRef<TypeaheadRef>(null);

  useImperativeHandle(composedRef, () => ({
    open: () => typeaheadRef.current?.open(),
    close: () => typeaheadRef.current?.close(),
    focus: () => typeaheadRef.current?.open(),
    blur: () => typeaheadRef.current?.close()
  }));

  useEffect(() => {
    if (!id || isDisabled) return;

    return registerFocusable(id, {
      focus: () => typeaheadRef.current?.open()
    });
  }, [id, isDisabled]);

  const handleChange = useCallback(
    (val?: any) => {
      let transformedValue = val;
      if (isFunction(valueTransform?.output) && val) {
        transformedValue = valueTransform.output(val);
      }
      field.onChange(transformedValue ?? null);
      onSelect?.(val ?? null);
    },
    [field, onSelect, valueTransform]
  );

  return (
    <Typeahead
      ref={typeaheadRef}
      value={value}
      onChange={handleChange}
      onOpenChange={value => {
        if (!value) field.onBlur();
      }}
      disabled={isDisabled}
      placeholder={placeholder}
      headerTitle={props.headerTitle ?? props.label}
      {...props}
    >
      <SelectTrigger
        id={id}
        disabled={isDisabled}
        invalid={fieldState.invalid}
        iconAfter={
          !isRequired && !!value ? (
            <X
              color={'$danger'}
              hitSlop={{ bottom: 12, left: 12, right: 12, top: 12 }}
              onPress={() => handleChange(undefined)}
            />
          ) : undefined
        }
      >
        <SelectTrigger.Text color={value ? '$bodyFont' : '$placeholderColor'}>
          {value ? (valueFormatter?.(value) ?? value) : placeholder}
        </SelectTrigger.Text>
      </SelectTrigger>
    </Typeahead>
  );
};

FormTypeaheadComponent.displayName = 'FormTypeaheadComponent';

export const FormTypeahead = withLabelAndValidation<TypeaheadRef, FormTypeaheadProps>(
  FormTypeaheadComponent
) as <TValue = any, TFieldValues extends FieldValues = FieldValues>(
  props: FormTypeaheadProps<TValue, TFieldValues>
) => React.ReactElement<FormTypeaheadProps<TValue, TFieldValues>>;
